// src/components/layout/AuthLayout.tsx
import React, { type ReactNode } from "react";
import { Link, Outlet } from "react-router-dom";
import { ArrowLeft, Sun, Moon } from "lucide-react";
import { useDarkMode } from "../../context/DarkModeContext";
import { useAuth } from "../../context/AuthContext";
import logo from "/findwork.jpg";

interface AuthLayoutProps {
  children?: ReactNode;
  showBackButton?: boolean;
  backTo?: string;
  backLabel?: string;
  title?: string;
  subtitle?: string;
}

const AuthLayout: React.FC<AuthLayoutProps> = ({
  children,
  showBackButton = true,
  backTo = "/",
  backLabel = "Back to home",
  title,
  subtitle,
}) => {
  const { isDark, toggleDarkMode } = useDarkMode();
  const { user } = useAuth();

  // Determine where to redirect back to
  const getBackPath = () => {
    if (backTo) return backTo;
    return user ? "/dashboard" : "/";
  };

  const getBackLabel = () => {
    if (backLabel) return backLabel;
    return user ? "Back to dashboard" : "Back to home";
  };

  return (
    <div
      className={`min-h-screen transition-colors duration-300 ${
        isDark ? "dark bg-gray-900" : "bg-gray-50"
      }`}
    >
      {/* Minimal Header */}
      <header
        className={`border-b ${isDark ? "border-gray-800" : "border-gray-200"}`}
      >
        <div className="max-w-7xl mx-auto py-4 px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between">
            {/* Logo Section */}
            <div className="flex items-center space-x-3">
              <Link to="/" className="flex items-center space-x-3 group">
                <img
                  src={logo}
                  alt="FindWork"
                  className="h-10 w-10 rounded-full border-2 border-sky-500 group-hover:border-sky-400 transition-colors duration-300"
                />
                <div className="flex flex-col">
                  <span className="text-2xl font-bold bg-gradient-to-r from-sky-500 to-sky-600 bg-clip-text text-transparent">
                    FindWork
                  </span>
                  <span
                    className={`text-xs ${isDark ? "text-gray-400" : "text-gray-500"} -mt-1 hidden sm:block`}
                  >
                    Find Your Dream Job
                  </span>
                </div>
              </Link>
            </div>

            {/* Right side - Dark mode toggle */}
            <div className="flex items-center space-x-3">
              <button
                onClick={() => toggleDarkMode()}
                className={`p-2 rounded-lg transition-colors duration-300 ${
                  isDark
                    ? "text-yellow-400 hover:bg-gray-800 hover:text-yellow-300"
                    : "text-gray-600 hover:bg-gray-100 hover:text-gray-800"
                }`}
                aria-label={
                  isDark ? "Switch to light mode" : "Switch to dark mode"
                }
              >
                {isDark ? (
                  <Sun className="w-5 h-5" />
                ) : (
                  <Moon className="w-5 h-5" />
                )}
              </button>

              {user && (
                <Link
                  to="/dashboard"
                  className={`px-3 py-1.5 text-sm rounded-lg font-medium transition-all duration-300 ${
                    isDark
                      ? "border-sky-400 text-sky-400 hover:bg-sky-400 hover:text-white"
                      : "border-sky-500 text-sky-600 hover:bg-sky-500 hover:text-white"
                  } border`}
                >
                  Dashboard
                </Link>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Main Content - Simplified: Just center the content */}
      <main
        className={`min-h-[calc(100vh-80px)] flex items-center justify-center p-4 ${
          isDark ? "bg-gray-900" : "bg-gray-50"
        }`}
      >
        <div className="w-full max-w-md">
          {/* Back Button */}
          {showBackButton && (
            <div className="mb-6">
              <Link
                to={getBackPath()}
                className={`inline-flex items-center text-sm ${
                  isDark
                    ? "text-gray-400 hover:text-gray-300"
                    : "text-gray-600 hover:text-gray-900"
                } transition-colors group`}
              >
                <ArrowLeft className="w-4 h-4 mr-2 group-hover:-translate-x-1 transition-transform" />
                {getBackLabel()}
              </Link>
            </div>
          )}

          {/* Auth Content - Remove the extra container and styling */}
          <div>
            {/* Optional title and subtitle */}
            {(title || subtitle) && (
              <div className="mb-8 text-center">
                {title && (
                  <h1
                    className={`text-2xl font-bold ${isDark ? "text-white" : "text-gray-900"} mb-2`}
                  >
                    {title}
                  </h1>
                )}
                {subtitle && (
                  <p
                    className={`text-sm ${isDark ? "text-gray-400" : "text-gray-600"}`}
                  >
                    {subtitle}
                  </p>
                )}
              </div>
            )}

            {children || <Outlet />}
          </div>

          {/* Additional Auth Options */}
          <div className="mt-8 text-center">
            <p
              className={`text-sm ${isDark ? "text-gray-400" : "text-gray-600"}`}
            >
              Need help?{" "}
              <Link
                to="/contact-support"
                className="text-sky-600 dark:text-sky-400 hover:text-sky-700 dark:hover:text-sky-300 font-medium"
              >
                Contact Support
              </Link>
            </p>
          </div>
        </div>
      </main>

      {/* Simple Footer for Auth Pages */}
      <footer
        className={`border-t ${isDark ? "border-gray-800" : "border-gray-200"} py-6`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
            <div
              className={`text-sm ${isDark ? "text-gray-400" : "text-gray-600"}`}
            >
              © {new Date().getFullYear()} FindWork. All rights reserved.
            </div>
            <div className="flex space-x-6">
              <Link
                to="/privacy-policy"
                className={`text-sm ${
                  isDark
                    ? "text-gray-400 hover:text-sky-400"
                    : "text-gray-600 hover:text-sky-600"
                } transition-colors`}
              >
                Privacy Policy
              </Link>
              <Link
                to="/terms-of-service"
                className={`text-sm ${
                  isDark
                    ? "text-gray-400 hover:text-sky-400"
                    : "text-gray-600 hover:text-sky-600"
                } transition-colors`}
              >
                Terms of Service
              </Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default AuthLayout;
